import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { TextFieldProps } from '@mui/material';
import { SelectOption } from 'global';
import { Control } from 'react-hook-form/dist/types/form';
import { FieldValues } from 'react-hook-form/dist/types/fields';
import { UseControllerProps } from 'react-hook-form/dist/types/controller';

import { FormInputSelect } from '../FormInputSelect';

interface Props<FormValues extends FieldValues = FieldValues> extends Omit<TextFieldProps, 'select' | 'onChange'> {
  label?: string;
  dataTestId?: string;
  control: Control<FormValues>;
  extraAction?: (nextValue: string) => void;
  name: UseControllerProps<FormValues>['name'];
  intervalMinutes?: 15 | 30 | 60;
}

const buildTimeOptions = (intervalMinutes: number): SelectOption[] => {
  const options: SelectOption[] = [];
  const totalSlots = (24 * 60) / intervalMinutes;

  for (let i = 0; i < totalSlots; i++) {
    const totalMinutes = i * intervalMinutes;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const value = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

    const period = hours < 12 ? 'AM' : 'PM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const label = `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;

    options.push({ value, label });
  }

  return options;
};

export const TimeSelectField = <FormValues extends FieldValues = FieldValues>({
  name,
  label,
  control,
  extraAction,
  dataTestId,
  intervalMinutes = 15,
  ...other
}: Props<FormValues>) => {
  const options = useMemo(() => buildTimeOptions(intervalMinutes), [intervalMinutes]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref, onChange, ...field }, fieldState }) => (
        <FormInputSelect
          label={label}
          variant="standard"
          options={options}
          dataTestId={dataTestId}
          {...other}
          {...field}
          value={field.value ?? ''}
          error={!!other?.error || !!fieldState.error}
          helperText={fieldState.error?.message ?? other?.helperText}
          inputRef={ref}
          onChange={value => {
            onChange(value);
            extraAction?.(String(value));
          }}
        />
      )}
    />
  );
};
